import React, { createContext, useContext, useState, useEffect } from 'react';
import axiosConfig from '../api/axiosConfig';

const SiteSettingsContext = createContext();

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (!context) {
    throw new Error('useSiteSettings must be used within a SiteSettingsProvider');
  }
  return context;
};

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    siteName: 'EduPlatform',
    siteTitle: 'Eğitimde Yeni Nesil',
    siteDescription: '',
    siteLogo: '',
    contactEmail: 'info@ozelderskurs.com.tr',
    contactPhone: '+90 (212) 123 45 67',
    contactAddress: 'İstanbul, Türkiye',
    facebookUrl: '',
    twitterUrl: '',
    instagramUrl: '',
    youtubeUrl: '',
    linkedinUrl: '',
    appStoreUrl: '',
    googlePlayUrl: '',
    footerCopyright: '© 2024 Özel Ders Kurs Sistemi. Tüm hakları saklıdır.',
    footerText: 'Modern eğitim anlayışıyla binlerce öğrenci ve öğretmeni buluşturan güvenilir platform.',
    studentCount: 10000,
    teacherCount: 500,
    satisfactionRate: 98,
    primaryColor: '#4F46E5',
    secondaryColor: '#7C3AED',
    maintenanceMode: false,
    maintenanceMessage: 'Site bakım modunda. Lütfen daha sonra tekrar deneyiniz.',
    language: 'tr'
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPublicSettings();
  }, []);

  const fetchPublicSettings = async () => {
    try {
      setLoading(true);
      const response = await axiosConfig.get('/admin/site-settings/public/settings');

      if (response.data.success) {
        setSettings(prevSettings => ({
          ...prevSettings,
          ...response.data.data
        }));
      }
    } catch (error) {
      console.error('Public site ayarları yüklenirken hata:', error);
      setError('Site ayarları yüklenirken hata oluştu');

      // Hata durumunda localStorage'dan ayarları almayı dene
      const cachedSettings = localStorage.getItem('siteSettings');
      if (cachedSettings) {
        try {
          const parsedSettings = JSON.parse(cachedSettings);
          setSettings(prevSettings => ({
            ...prevSettings,
            ...parsedSettings
          }));
        } catch (parseError) {
          console.error('Cached settings parse error:', parseError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Ayarları localStorage'a kaydet
  useEffect(() => {
    if (!loading) {
      localStorage.setItem('siteSettings', JSON.stringify(settings));
    }
  }, [settings, loading]);

  // Ayarları yenile
  const refreshSettings = () => {
    fetchPublicSettings();
  };

  // Belirli bir ayarı güncelle (sadece context'te, server'da değil)
  const updateSetting = (key, value) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [key]: value
    }));
  };

  // Tema renklerini CSS variable olarak ayarla
  useEffect(() => {
    if (settings.primaryColor && settings.secondaryColor) {
      document.documentElement.style.setProperty('--primary-color', settings.primaryColor);
      document.documentElement.style.setProperty('--secondary-color', settings.secondaryColor);
    }
  }, [settings.primaryColor, settings.secondaryColor]);

  // Sayfa başlığını güncelle
  useEffect(() => {
    if (settings.siteName && settings.siteTitle) {
      document.title = `${settings.siteName} - ${settings.siteTitle}`;
    }
  }, [settings.siteName, settings.siteTitle]);

  // Meta description'ı güncelle
  useEffect(() => {
    if (settings.siteDescription) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', settings.siteDescription);
      } else {
        const newMeta = document.createElement('meta');
        newMeta.name = 'description';
        newMeta.content = settings.siteDescription;
        document.getElementsByTagName('head')[0].appendChild(newMeta);
      }
    }
  }, [settings.siteDescription]);

  const value = {
    settings,
    loading,
    error,
    refreshSettings,
    updateSetting
  };

  // Bakım modu kontrolü
  if (settings.maintenanceMode && !window.location.pathname.includes('/admin')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded-lg shadow-lg">
          <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Bakım Modu</h1>
          <p className="text-gray-600 mb-6">
            {settings.maintenanceMessage}
          </p>
          <p className="text-sm text-gray-500">
            En kısa sürede geri döneceğiz!
          </p>
        </div>
      </div>
    );
  }

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
};